/**
 * Pacing toward a target date (v8.0.0, Blueprint §12). PURE.
 *
 * "$X a month gets you there by March" — from the target, the balance and the
 * date ONLY. A plain division of what is left by the time that is left: never a
 * forecast of income, never a judgement of whether the operator can afford it.
 *
 * Shown only when every input is known and there is actually something to pace:
 * a target, a date still ahead, a known balance below the target. Anything else
 * returns null and the line is simply absent — an unknown is never rounded into
 * a number (CLAUDE.md, Architecture 3).
 */

import { daysUntil } from './due-date';
import { formatMoney } from './envelope-math';

/** Average days in a month (365.25 / 12). */
const DAYS_PER_MONTH = 30.4375;

export interface Pace {
  /** What is left to set aside. Always > 0. */
  remainingMinor: number;
  /** Per month, rounded UP to the cent so the last month is never short. NULL under a month. */
  perMonthMinor: number | null;
  daysLeft: number;
}

export function paceToTarget(
  input: { balanceMinor: number | null; targetMinor: number | null; targetDate?: string | null },
  now: Date = new Date(),
): Pace | null {
  const { balanceMinor, targetMinor, targetDate } = input;
  if (balanceMinor == null || targetMinor == null || targetMinor <= 0) return null;
  const remainingMinor = targetMinor - Math.max(0, balanceMinor);
  if (remainingMinor <= 0) return null;
  const daysLeft = daysUntil(targetDate, now);
  if (daysLeft == null || daysLeft <= 0) return null;

  const months = daysLeft / DAYS_PER_MONTH;
  // Under a month there is no "per month" — just what is left, by the date.
  const perMonthMinor = months < 1 ? null : Math.ceil(remainingMinor / months);
  return { remainingMinor, perMonthMinor, daysLeft };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Mar 15", or "Mar 15, 2027" when it is not this year. */
export function shortDate(ymd: string, now: Date = new Date()): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  const label = `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}`;
  return Number(m[1]) === now.getFullYear() ? label : `${label}, ${m[1]}`;
}

/** The sentence, or null when there is nothing honest to say. */
export function paceText(
  input: { balanceMinor: number | null; targetMinor: number | null; targetDate?: string | null },
  now: Date = new Date(),
): string | null {
  const pace = paceToTarget(input, now);
  if (!pace || !input.targetDate) return null;
  const by = shortDate(input.targetDate, now);
  return pace.perMonthMinor == null
    ? `${formatMoney(pace.remainingMinor)} more gets you there by ${by}.`
    : `${formatMoney(pace.perMonthMinor)} a month gets you there by ${by}.`;
}
